import { ComponentConfig } from '@measured/puck';
import { MarginProps, marginFields } from '../../puck/reusable-props/margin';
import CustomTooltip from '../../../components/common/tooltip';
import { ReduxSelectorProps } from '../../services/constants';
import './../text-editor/style.scss';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
} from '../../utils/helper';
import { paddingFields, PaddingProps } from '../../puck/reusable-props/padding';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import WidthComponent, { IWidthComponentProps } from '../blocks/widthComponent';
import { IAsset } from '../../interface/apiServiceInterface';
import { imageField } from '../../puck/reusable-props/image';
import { AxiosInstance } from 'axios';

export type VideoPlayerProps = {
  imageFile: IAsset;
  autoPlay: boolean;
  muted: boolean;
  loop: boolean;
  controls: boolean;
  progressBar: boolean;
  maxWidth: IWidthComponentProps;
  border: IBorderValueProps;
} & MarginProps &
  PaddingProps;

export interface VideoUploaderComponentProps extends ReduxSelectorProps {
  baseService: AxiosInstance;
}

export const VideoPlayer = ({
  useAppSelector,
  baseService,
}: VideoUploaderComponentProps): ComponentConfig<VideoPlayerProps> => {
  return {
    label: (
      <CustomTooltip title="VideoPlayer">
        <span>Video Player</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      imageFile: { ...imageField(true, baseService).file },
      autoPlay: {
        type: 'select',
        label: 'Auto Play',
        options: [
          { label: 'On', value: true },
          { label: 'Off', value: false },
        ],
      },
      muted: {
        type: 'select',
        label: 'Muted',
        options: [
          { label: 'On', value: true },
          { label: 'Off', value: false },
        ],
      },
      loop: {
        type: 'select',
        label: 'Loop',
        options: [
          { label: 'On', value: true },
          { label: 'Off', value: false },
        ],
      },
      controls: {
        type: 'select',
        label: 'Controls',
        options: [
          { label: 'On', value: true },
          { label: 'Off', value: false },
        ],
      },
      progressBar: {
        type: 'select',
        label: 'Progress Bar',
        options: [
          { label: 'On', value: true },
          { label: 'Off', value: false },
        ],
      },
      maxWidth: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <WidthComponent onChange={onChange} value={value} title="Max width" />
        ),
      },
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
    },
    defaultProps: {
      ...borderProps,
      autoPlay: true,
      muted: true,
      loop: true,
      controls: false,
      progressBar: true,
      maxWidth: {
        deviceType: 'desktop',
        width: {
          desktop: 'none',
          tablet: 'none',
          mobile: 'none',
        },
      },
      imageFile: {
        code: '',
        imageUrl: '',
        fileName: '',
        storeID: '',
      },
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
    },
    render: ({
      editMode,
      autoPlay,
      controls,
      loop,
      muted,
      progressBar,
      maxWidth,
      imageFile,
      border,
      margin,
      padding,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      let finalMaxWidth: string;
      if (maxWidth?.width?.[currentDeviceType]?.includes('%')) {
        finalMaxWidth = maxWidth?.width?.[currentDeviceType];
      } else if (maxWidth?.width?.[currentDeviceType]) {
        finalMaxWidth = `${maxWidth?.width?.[currentDeviceType]}px`;
      } else {
        finalMaxWidth = 'none'; // handled for BC
      }
      return (
        <div
          style={{
            ...marginStyles,
            ...getPaddingStyles(padding, currentDeviceType),
            ...getBorderProps(border, currentDeviceType),
          }}>
          <video
            style={{
              maxWidth: finalMaxWidth,
            }}
            key={imageFile?.imageUrl}
            controls={controls}
            muted={muted}
            loop={loop}
            autoPlay={autoPlay}>
            <source
              src={`${process.env.REACT_APP_S3_BUCKET_URL}${imageFile?.imageUrl}`}
              type="video/mp4"
            />
            <style>
              {`video::-webkit-media-controls-timeline {
                display: ${progressBar ? 'block' : 'none'};
              }`}
            </style>
          </video>
        </div>
      );
    },
  };
};
